/**
 * Maps a request URL to a stable, low-cardinality endpoint key for Datadog
 * (`@context.endpoint`): API Gateway templates for our API, `ext:*` for the rest.
 * Never returns emails, UUIDs or free text.
 */

// API Gateway paths used by the browser (plan Annex A.1).
// Keep in sync with ConfigService `url*` getters; api-endpoint.util.spec.ts checks it.
export const API_ROUTE_TEMPLATES: readonly string[] = [
  '/api/v2/admin/accounts',
  '/api/v2/admin/alerts',
  '/api/v2/admin/alerts/{alertId}',
  '/api/v2/admin/assignments/{id}/resend',
  '/api/v2/admin/auth/me',
  '/api/v2/admin/candidates',
  '/api/v2/admin/candidates/{email}/evolution',
  '/api/v2/admin/candidates/{email}/history',
  '/api/v2/admin/configurations',
  '/api/v2/admin/configurations/{id}',
  '/api/v2/admin/dashboard/stats',
  '/api/v2/admin/feedback',
  '/api/v2/admin/knowledge-areas',
  '/api/v2/admin/maintenance',
  '/api/v2/admin/process',
  '/api/v2/admin/process-records',
  '/api/v2/admin/process/{process_id}',
  '/api/v2/admin/process/{process_id}/send',
  '/api/v2/admin/profiles',
  '/api/v2/admin/profiles/{profile_id}',
  '/api/v2/admin/profiles/{profile_id}/knowledges',
  '/api/v2/admin/profiles/{profile_id}/knowledges/{knowledge_name}',
  '/api/v2/admin/reports',
  '/api/v2/admin/reports/{assignment_id}',
  '/api/v2/admin/reports/{assignment_id}/profile-comparison',
  '/api/v2/admin/send-test',
  '/api/v2/admin/sparring',
  '/api/v2/admin/tags',
  '/api/v2/admin/test',
  '/api/v2/admin/test-plan-drafts',
  '/api/v2/admin/test-plan-drafts/{draftId}',
  '/api/v2/admin/test-records',
  '/api/v2/admin/test-records/{assignmentId}/progress',
  '/api/v2/admin/test-records/{assignmentId}/reset',
  '/api/v2/admin/test-records/{assignmentId}/status',
  '/api/v2/admin/test/{test_id}',
  '/api/v2/admin/usage-metrics',
  '/api/v2/admin/users',
  '/api/v2/alerts',
  '/api/v2/answers-progress/{id}',
  '/api/v2/artifact-review/analyze',
  '/api/v2/conversational/presign',
  '/api/v2/decisions',
  '/api/v2/fc/low',
  '/api/v2/feedback',
  '/api/v2/identity/confirm',
  '/api/v2/identity/document/upload',
  '/api/v2/maintenance/status',
  '/api/v2/photos/add',
  '/api/v2/process/{process_assignment_id}',
  '/api/v2/process/{process_assignment_id}/onboarding',
  '/api/v2/push-subscriptions',
  '/api/v2/recordings/presigned',
  '/api/v2/sparring/bot',
  '/api/v2/test/finish',
  '/api/v2/test/verify/{id}',
  '/api/v2/test/{id}',
  '/api/v2/test/{id}/pre-test-state',
  '/api/v2/test/{id}/pre-test-step',
];

const API_PREFIX = '/api/v2/';
const TEMPLATE_SEGMENTS = API_ROUTE_TEMPLATES.map((t) => t.split('/'));
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isDynamicSegment(seg: string): boolean {
  return UUID.test(seg) || /^\d+$/.test(seg) || /@|%40/i.test(seg) || seg.length > 24;
}

function matchTemplate(segments: string[]): string[] | undefined {
  let best: string[] | undefined;
  let bestLiterals = -1;
  for (const tpl of TEMPLATE_SEGMENTS) {
    if (tpl.length !== segments.length) continue;
    let literals = 0;
    const ok = tpl.every((t, i) => {
      if (t.startsWith('{')) return true;
      if (t !== segments[i]) return false;
      literals++;
      return true;
    });
    if (ok && literals > bestLiterals) {
      best = tpl;
      bestLiterals = literals;
    }
  }
  return best;
}

export function normalizeApiEndpoint(
  url: string,
  init?: { headers?: { get(name: string): string | null } },
): string {
  let parsed: URL;
  try {
    parsed = new URL(url, location.origin);
  } catch {
    return 'unknown';
  }

  const apiIdx = parsed.pathname.indexOf(API_PREFIX);
  if (apiIdx < 0) {
    const host = parsed.hostname;
    if (host.startsWith('cognito-idp.')) {
      const op = init?.headers?.get('X-Amz-Target')?.split('.').pop();
      return op ? `ext:cognito-idp:${op}` : 'ext:cognito-idp';
    }
    // ponytail: S3 usage (recordings-put / photos-put) is not derivable from a presigned URL; callers that know it can pass their own key.
    if (/(^|\.)s3[.-]/.test(host)) return 'ext:s3';
    return `ext:${host.slice(0, 40)}`;
  }

  const segments = parsed.pathname.slice(apiIdx).replace(/\/$/, '').split('/');
  const template = matchTemplate(segments);
  if (template) return template.join('/');
  return segments.map((s) => (isDynamicSegment(s) ? '{id}' : s)).join('/');
}

export function toEndpointKey(method: string, url: string): string {
  return `${method} ${normalizeApiEndpoint(url)}`;
}

/**
 * True for a request to the app's own static assets (config.json, local-services.json, i18n...):
 * same origin and not under the API prefix. Those are not API calls and must not raise `api_error`
 * (on CloudFront a missing asset falls back to index.html with 200).
 */
export function isSameOriginAssetRequest(url: string): boolean {
  try {
    const parsed = new URL(url, location.origin);
    return parsed.origin === location.origin && !parsed.pathname.includes(API_PREFIX);
  } catch {
    return false;
  }
}
